// Gelo Minimal: Kollektions- und Suchseite (sections/gelo-kollektion.liquid, gelo-kollektion-suche.liquid).
// Klicks per Delegation am Dokument, Anpassungen an Horizons Teilen nach jedem Neuzeichnen des Abschnitts (Filter,
// Sortierung: Horizon morpht den Abschnitt, assets/facets.js) über einen MutationObserver am Abschnitt.
// 1. „Weitere n Teile zeigen“ / „Vorherige Teile zeigen“: holt die Seite über die Section Rendering API und setzt die
//    Karten dahinter bzw. davor (Fokus auf die erste neue Karte, Ansage für Screenreader). Ohne Skript oder bei einem
//    Fehler sind es normale Seitenlinks; Filter und Sortierung stecken schon in deren Adresse.
// 2. „Sortieren“ (unter 990 px): eigenes Blatt von unten wie im Entwurf, liest Horizons Sortierung und setzt sie dort
//    (Klick auf Horizons Radio, Horizon zeichnet neu). Die Sortierung in Horizons Filter-Schublade ist ausgeblendet.
// 3. Klick auf eine Karte merkt sich Seite und Karte in der Adresse (?page=n#Karte): „Zurück“ landet wieder dort.
// 4. Kategorien: unter 990 px mit Filtern als Gruppe „Kategorie“ oben in Horizons Schublade (aus dem template der
//    Sektion, nach jedem Neuzeichnen wieder eingesetzt); ohne Filter als Wischzeile, die gewählte wird nur dann nach
//    vorn gerückt, wenn sie sonst nicht ganz zu sehen wäre (auf eine Chip-Grenze).
// 5. Texte wie im Entwurf: Sortiernamen („Empfohlen“, „Preis aufsteigend“ …, nur die vier des Entwurfs plus Standard und
//    aktuelle Wahl), aktuelle Sortierung grau im Knopf, Schublade „Filter“, „n Teile zeigen“, „Zurücksetzen“.
//    (Ohne Eingriff in Horizons locales/de.json; bauen_kollektion.py --sprache macht es dort, dann bleibt das hier ohne Wirkung.)
// 6. Adresse: Schublade und Leiste schicken beide sort_by (doppelt in der Adresse), die Suche verliert beim Filtern
//    type=product und options[prefix] (dann zählt Shopify Seiten und Beiträge mit). Beides vor dem Neuzeichnen richten.
// 7. Lange Beschreibung: „Mehr lesen“ / „Weniger zeigen“.
// 8. Suche: passende Seiten und Beiträge als kurze Links (Shopifys Schnellsuche, nur Titel).
// 9. Zurück/Vor im Browser nach Filter oder Sortierung: Horizon (assets/facets.js) schreibt die Adresse per pushState,
//    zeichnet bei popstate aber nicht neu. Hier wird der Abschnitt dann für die neue Adresse neu geholt (Section
//    Rendering API über Horizons sectionRenderer); reine Sprungmarken-Wechsel („Stelle merken“) bleiben unberührt.
// 10. Sortier-Liste ab 990 px per Tastatur: Enter und Leertaste auf einer Option wählen sie (Horizons handleKeyDown
//    findet die Option nicht, siehe unten).
// Kauf auf der Karte, Plus und Aufklappen: assets/gelo-minimal.js.

import { lockScroll, unlockScroll } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

const DESK = matchMedia('(min-width: 990px)');
const RUHIG = matchMedia('(prefers-reduced-motion: reduce)');

function melden(sektion, text) {
  const meldung = sektion?.querySelector('[data-gelo-meldung]');
  if (!meldung) return;
  meldung.textContent = '';
  requestAnimationFrame(() => {
    meldung.textContent = text;
  });
}

// Scrollbereich der Seite: ab 990 px .page-wrapper (Horizon), darunter das Dokument
function scrollBereich() {
  const bereich = document.querySelector('.page-wrapper');
  const scrollt = bereich && bereich.scrollHeight > bereich.clientHeight && getComputedStyle(bereich).overflowY !== 'visible';
  return scrollt ? bereich : document.scrollingElement || document.documentElement;
}

// Element unter Kopf und (ab 990 px klebender) Filterleiste in den Blick holen
function inDenBlick(element, bereich = scrollBereich()) {
  const leiste = element.closest('[data-gelo-sektion]')?.querySelector('.gm-filterleiste');
  const kopf = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  const oben = DESK.matches && leiste ? leiste.getBoundingClientRect().bottom : kopf;
  bereich.scrollBy({ top: element.getBoundingClientRect().top - oben - 16, behavior: 'instant' });
}

// ---------- 1. Seiten nachladen ----------
async function seiteLaden(link, davor) {
  const sektion = link.closest('[data-gelo-sektion]');
  const raster = sektion?.querySelector('[data-gelo-raster]');
  if (!sektion || !raster) {
    location.href = link.href;
    return;
  }
  if (link.getAttribute('aria-busy') === 'true') return;
  link.setAttribute('aria-busy', 'true');

  const url = new URL(link.href, location.href);
  url.searchParams.set('section_id', sektion.dataset.geloSektion);
  try {
    const antwort = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!antwort.ok) throw new Error(`HTTP ${antwort.status}`);
    const neu = new DOMParser().parseFromString(await antwort.text(), 'text/html');
    // doppelte Karten (Sortierung hat sich zwischendurch geändert) überspringen
    const teile = [...neu.querySelectorAll('[data-gelo-raster] > li')].filter((li) => !li.id || !document.getElementById(li.id));
    // Davor einsetzen: die Scroll-Verankerung des Browsers würde die alte Stelle halten und gegen inDenBlick arbeiten
    const bereich = scrollBereich();
    if (davor) {
      bereich.style.overflowAnchor = 'none';
      raster.prepend(...teile);
    } else {
      raster.append(...teile);
    }

    const art = davor ? '[data-gelo-zurueck-block]' : '[data-gelo-mehr-block]';
    const block = link.closest(art);
    const neuerBlock = neu.querySelector(art);
    if (neuerBlock) block?.replaceWith(neuerBlock);
    else block?.remove();

    if (!davor) history.replaceState(history.state, '', link.href);
    const erste = teile[0];
    if (erste) {
      if (davor) {
        inDenBlick(erste, bereich);
        requestAnimationFrame(() => requestAnimationFrame(() => (bereich.style.overflowAnchor = '')));
      }
      erste.querySelector('.gm-karte__titel a')?.focus({ preventScroll: true });
    } else {
      bereich.style.overflowAnchor = '';
    }
    const n = teile.length;
    melden(sektion, n === 1 ? (davor ? 'Ein Teil davor geladen.' : 'Ein weiteres Teil geladen.') : `${n} ${davor ? 'Teile davor' : 'weitere Teile'} geladen.`);
  } catch (fehler) {
    console.warn('[gelo-kollektion] Nachladen nicht möglich, lade die Seite', fehler);
    location.href = link.href;
  } finally {
    link.removeAttribute('aria-busy');
  }
}

// ---------- 2. und 5. Sortierung: Namen, sichtbare Wahl, Blatt ----------
const SORTIERUNG = {
  manual: 'Empfohlen',
  relevance: 'Beste Treffer',
  'most-relevant': 'Relevanz',
  'best-selling': 'Meistverkauft',
  'title-ascending': 'Name A–Z',
  'title-descending': 'Name Z–A',
  'price-ascending': 'Preis aufsteigend',
  'price-descending': 'Preis absteigend',
  'created-ascending': 'Älteste zuerst',
  'created-descending': 'Neu zuerst',
};
// wie im Entwurf (Empfohlen, Preis aufsteigend, Preis absteigend, Neu zuerst; Suche: Beste Treffer), dazu immer der
// Standard der Kollektion und die gerade gewählte Sortierung. „Empfohlen“ ist der Standard der Kollektion, wenn er
// eine Auswahl ist (von Hand, meistverkauft, Relevanz); sonst die Reihenfolge von Hand.
const SORTIERUNG_IMMER = ['relevance', 'price-ascending', 'price-descending', 'created-descending'];
const EMPFOHLEN = ['manual', 'best-selling', 'most-relevant'];

function sortierName(wert, standard, name) {
  if (wert === standard && EMPFOHLEN.includes(wert)) return 'Empfohlen';
  return SORTIERUNG[wert] || name || wert;
}

function sortierSichtbar(wert, standard, aktuell) {
  if (wert === standard || wert === aktuell) return true;
  if (wert === 'manual') return !EMPFOHLEN.includes(standard);
  return SORTIERUNG_IMMER.includes(wert);
}

// Sortierungen einer Sektion aus Horizons waagerechter Leiste: [{ wert, name, gewaehlt }]
function sortierungen(sektion) {
  const komponente = sektion.querySelector('.facets--horizontal sorting-filter-component') || sektion.querySelector('sorting-filter-component');
  const standard = komponente?.querySelector('details[data-default-sort-by]')?.dataset.defaultSortBy;
  const radios = [...(komponente?.querySelectorAll('input[type="radio"][name="sort_by"]') ?? [])];
  const aktuell = radios.find((radio) => radio.checked)?.value ?? standard;
  return radios
    .filter((radio) => sortierSichtbar(radio.value, standard, aktuell))
    .map((radio) => ({ wert: radio.value, name: sortierName(radio.value, standard, radio.dataset.optionName), gewaehlt: radio.value === aktuell, radio }));
}

function sortierungAnpassen(root) {
  root.querySelectorAll('sorting-filter-component').forEach((komponente) => {
    const standard = komponente.querySelector('details[data-default-sort-by]')?.dataset.defaultSortBy;
    const radios = [...komponente.querySelectorAll('input[type="radio"][name="sort_by"]')];
    const aktuell = radios.find((radio) => radio.checked)?.value ?? standard;
    for (const radio of radios) {
      const name = SORTIERUNG[radio.value] && sortierName(radio.value, standard);
      const option = radio.closest('.sorting-filter__option');
      const text = option?.querySelector('.sorting-filter__label');
      if (name && text && text.textContent !== name) text.textContent = name;
      if (name && radio.dataset.optionName !== name) radio.dataset.optionName = name;
      // nicht gezeigte Sortierungen raus aus Liste und Tastaturweg (Horizon wandert über [role="option"])
      const zeigen = sortierSichtbar(radio.value, standard, aktuell);
      const zeile = radio.closest('label');
      if (zeile && zeile.hidden === zeigen) zeile.hidden = !zeigen;
      if (option && option.hasAttribute('role') !== zeigen) {
        if (zeigen) option.setAttribute('role', 'option');
        else option.removeAttribute('role');
      }
    }
    const status = komponente.querySelector('summary .facets__status');
    const wert = aktuell ? sortierName(aktuell, standard, status?.textContent.trim()) : '';
    if (status && wert && status.textContent.trim() !== wert) status.textContent = wert;
    komponente.querySelectorAll('select[name="sort_by"] option').forEach((option) => {
      const name = SORTIERUNG[option.value] && sortierName(option.value, standard);
      if (name && option.textContent.trim() !== name) option.textContent = name;
    });
  });
}

let blatt = null;
let blattSektion = null;
let blattAusloeser = null;

function blattBauen() {
  blatt = document.createElement('dialog');
  blatt.className = 'gm-blatt';
  blatt.setAttribute('aria-labelledby', 'gm-blatt-titel');
  blatt.innerHTML = `
    <div class="gm-blatt__kopf">
      <h2 class="gm-blatt__titel" id="gm-blatt-titel" tabindex="-1">Sortieren</h2>
      <button type="button" class="gm-blatt__zu" data-gelo-blatt-zu>
        Schließen
        <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.25" aria-hidden="true" focusable="false"><path d="M5 5l10 10M15 5 5 15"/></svg>
      </button>
    </div>
    <fieldset class="gm-blatt__liste">
      <legend class="visually-hidden">Sortieren nach</legend>
      <div data-gelo-blatt-optionen></div>
    </fieldset>`;
  document.body.append(blatt);

  blatt.addEventListener('cancel', (ereignis) => {
    ereignis.preventDefault();
    blattSchliessen();
  });
  blatt.addEventListener('click', (ereignis) => {
    // Klick auf den Schleier (das dialog-Element selbst, außerhalb des Inhalts) oder auf „Schließen“
    if (ereignis.target === blatt || (ereignis.target instanceof Element && ereignis.target.closest('[data-gelo-blatt-zu]'))) blattSchliessen();
  });
  blatt.addEventListener('change', (ereignis) => {
    const eingabe = ereignis.target;
    if (!(eingabe instanceof HTMLInputElement) || eingabe.name !== 'gm-sortierung') return;
    sortieren(eingabe.value);
  });
}

function blattOeffnen(knopf) {
  const sektion = knopf.closest('[data-gelo-sektion]');
  if (!sektion) return;
  const liste = sortierungen(sektion);
  if (!liste.length) return;
  if (!blatt) blattBauen();
  blattSektion = sektion;
  blattAusloeser = knopf;

  const optionen = blatt.querySelector('[data-gelo-blatt-optionen]');
  optionen.replaceChildren(
    ...liste.map(({ wert, name, gewaehlt }) => {
      const zeile = document.createElement('label');
      zeile.className = 'gm-blatt__zeile';
      const eingabe = document.createElement('input');
      eingabe.type = 'radio';
      eingabe.name = 'gm-sortierung';
      eingabe.value = wert;
      eingabe.checked = gewaehlt;
      const text = document.createElement('span');
      text.textContent = name;
      zeile.append(eingabe, text);
      return zeile;
    })
  );
  blatt.classList.remove('is-zu');
  blatt.showModal();
  lockScroll(blatt);
  knopf.setAttribute('aria-expanded', 'true');
  blatt.querySelector('input:checked')?.focus();
}

function blattSchliessen({ fokus = true } = {}) {
  if (!blatt?.open || blatt.classList.contains('is-zu')) return;
  const fertig = () => {
    blatt.classList.remove('is-zu');
    blatt.close();
    unlockScroll(blatt);
    // der Knopf kann inzwischen neu gezeichnet sein
    const knopf = blattAusloeser?.isConnected ? blattAusloeser : blattSektion?.querySelector('[data-gelo-sortieren]');
    knopf?.setAttribute('aria-expanded', 'false');
    if (fokus) knopf?.focus({ preventScroll: true });
  };
  if (RUHIG.matches) {
    fertig();
    return;
  }
  blatt.classList.add('is-zu');
  let erledigt = false;
  const einmal = () => {
    if (erledigt) return;
    erledigt = true;
    fertig();
  };
  blatt.addEventListener('animationend', einmal, { once: true });
  setTimeout(einmal, 400);
}

function sortieren(wert) {
  const sektion = blattSektion;
  const eintrag = sektion && sortierungen(sektion).find((e) => e.wert === wert);
  blattSchliessen();
  if (!sektion || !eintrag || eintrag.gewaehlt) return;
  // Horizons Radio der waagerechten Leiste: löst dort updateFilterAndSorting aus (Adresse, Neuzeichnen)
  if (eintrag.radio?.isConnected) {
    eintrag.radio.click();
  } else {
    const adresse = new URL(location.href);
    adresse.searchParams.set('sort_by', wert);
    adresse.searchParams.delete('page');
    location.href = adresse.toString();
    return;
  }
  melden(sektion, `Sortiert: ${eintrag.name}.`);
}

// ---------- 5. Texte der Filter-Schublade und der Leiste ----------
function textSetzen(element, text) {
  if (element.textContent.trim() !== text) element.textContent = text;
}

function schubladeAnpassen(root) {
  const titel = root.querySelector('#filters-drawer-heading');
  const knoten = titel && [...titel.childNodes].find((kind) => kind.nodeType === Node.TEXT_NODE && kind.data.trim());
  if (knoten && knoten.data.trim() !== 'Filter') knoten.data = knoten.data.replace(knoten.data.trim(), 'Filter');
  root.querySelectorAll('.facets__see-results').forEach((knopf) => {
    const ziffern = knopf.textContent.replace(/\D/g, '');
    if (!ziffern) return;
    const n = Number(ziffern);
    textSetzen(knopf, n === 1 ? '1 Teil zeigen' : `${n} Teile zeigen`);
  });
  root.querySelectorAll('.facets__clear-all, .facets__clear-all-link').forEach((knopf) => textSetzen(knopf, 'Zurücksetzen'));
}

// ---------- 7. Lange Beschreibung ----------
const offeneTexte = new Set();
const kurzeTexte = new WeakMap(); // einmal gemessen: passt der Text ohnehin in drei Zeilen?

function texteAnpassen(root) {
  root.querySelectorAll('[data-gelo-text].gm-kollektion__text--lang').forEach((text) => {
    const knopf = text.querySelector('[data-gelo-mehrtext]');
    const offen = offeneTexte.has(text.id);
    if (text.classList.contains('ist-offen') !== offen) text.classList.toggle('ist-offen', offen);
    if (knopf) {
      if (knopf.getAttribute('aria-expanded') !== String(offen)) knopf.setAttribute('aria-expanded', String(offen));
      textSetzen(knopf, offen ? 'Weniger zeigen' : 'Mehr lesen');
    }
    // passt der Text schon in drei Zeilen (breiter Bildschirm), entfällt das Kürzen
    if (!kurzeTexte.has(text) && !offen) {
      const inhalt = text.querySelector('.gm-kollektion__textinhalt');
      if (inhalt) kurzeTexte.set(text, inhalt.scrollHeight <= inhalt.clientHeight + 4);
    }
    const kurz = kurzeTexte.get(text) === true;
    if (text.classList.contains('ist-kurz') !== kurz) text.classList.toggle('ist-kurz', kurz);
  });
}

// ---------- 8. Suche: Seiten und Beiträge ----------
const seitenCache = new Map();

function seitenLaden(q) {
  if (!seitenCache.has(q)) {
    const basis = window.Theme?.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(`${basis}.json`, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'page,article');
    url.searchParams.set('resources[limit]', '3');
    url.searchParams.set('resources[options][fields]', 'title');
    seitenCache.set(
      q,
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((antwort) => (antwort.ok ? antwort.json() : null))
        .then((daten) => ({ seiten: daten?.resources?.results?.pages ?? [], beitraege: daten?.resources?.results?.articles ?? [] }))
        .catch(() => ({ seiten: [], beitraege: [] }))
    );
  }
  return seitenCache.get(q);
}

function seitenZeile(art, eintraege) {
  const zeile = document.createElement('p');
  zeile.className = 'gm-suche__seitenzeile';
  const kicker = document.createElement('span');
  kicker.className = 'gm-suche__art';
  kicker.textContent = `${art}:`;
  zeile.append(kicker, ' ');
  eintraege.forEach((eintrag, i) => {
    if (i > 0) zeile.append(' · ');
    const link = document.createElement('a');
    link.href = eintrag.url;
    link.textContent = eintrag.title;
    zeile.append(link);
  });
  return zeile;
}

function seitenAnpassen(root) {
  root.querySelectorAll('[data-gelo-seiten]').forEach(async (feld) => {
    const q = feld.dataset.q?.trim();
    if (!q || feld.dataset.gefuellt === q) return;
    feld.dataset.gefuellt = q;
    const { seiten, beitraege } = await seitenLaden(q);
    if (!feld.isConnected || feld.dataset.gefuellt !== q) return;
    const zeilen = [];
    if (seiten.length) zeilen.push(seitenZeile(seiten.length === 1 ? 'Seite' : 'Seiten', seiten));
    if (beitraege.length) zeilen.push(seitenZeile(beitraege.length === 1 ? 'Beitrag' : 'Beiträge', beitraege));
    feld.replaceChildren(...zeilen);
    feld.hidden = zeilen.length === 0;
  });
}

// ---------- 4. Kategorien in der Schublade ----------
// Am Ende von Horizons Zeile der gewählten Filter (.facets-remove), per CSS ganz oben: steht sie dort, entfernt Horizons
// Neuzeichnen (morph) nur sie selbst; weiter vorn würde es alle Filtergruppen dahinter verschieben (Fokus ginge verloren).
function kategorienInsBlatt(root) {
  const vorlage = root.querySelector('template[data-gelo-kategorien-blatt]');
  const ziel = root.querySelector('#filters-drawer .facets-drawer__filters .facets-remove');
  if (!(vorlage instanceof HTMLTemplateElement) || !ziel || ziel.querySelector(':scope > .gm-kategorien-blatt')) return;
  const gruppe = vorlage.content.querySelector('.gm-kategorien-blatt');
  if (gruppe) ziel.append(gruppe.cloneNode(true));
}

// ---------- Anpassen nach jedem Neuzeichnen ----------
function anpassen(root) {
  // Sicherheitsnetz: Horizon hat eine Suche ohne type=product gezeichnet
  const umleiten = root.querySelector('[data-gelo-umleiten]');
  if (umleiten instanceof HTMLAnchorElement && new URL(location.href).searchParams.get('type') !== 'product') {
    location.replace(umleiten.href);
    return;
  }
  sortierungAnpassen(root);
  kategorienInsBlatt(root);
  schubladeAnpassen(root);
  texteAnpassen(root);
  seitenAnpassen(root);
}

const BEOBACHTEN = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'role', 'checked', 'class'] };

function beobachten(sektion) {
  const root = sektion.closest('.shopify-section') || sektion;
  if (root.dataset.geloBeobachtet) return;
  root.dataset.geloBeobachtet = 'ja';
  const beobachter = new MutationObserver(() => {
    beobachter.disconnect();
    anpassen(root);
    beobachter.observe(root, BEOBACHTEN);
  });
  anpassen(root);
  beobachter.observe(root, BEOBACHTEN);
}

// ---------- 6. Adresse säubern, bevor Horizon neu zeichnet ----------
function adresseSaeubern(url) {
  const sektion = document.querySelector('[data-gelo-sektion]');
  if (!sektion) return url;
  let adresse;
  try {
    adresse = new URL(String(url), location.href);
  } catch {
    return url;
  }
  if (adresse.origin !== location.origin || adresse.pathname !== location.pathname) return url;
  const sortierung = adresse.searchParams.getAll('sort_by');
  if (sortierung.length > 1) adresse.searchParams.set('sort_by', sortierung[0]);
  if (sektion.classList.contains('gm-suche') && adresse.searchParams.has('q')) {
    adresse.searchParams.set('type', 'product');
    adresse.searchParams.delete('options');
    adresse.searchParams.set('options[prefix]', 'last');
  }
  return adresse.toString();
}
const pushState = history.pushState;
history.pushState = function (zustand, titel, url) {
  const ergebnis = pushState.call(this, zustand, titel, url == null ? url : adresseSaeubern(url));
  gezeichnet = suchSchluessel(location);
  return ergebnis;
};

// ---------- 9. Zurück/Vor nach Filter oder Sortierung ----------
// Vergleich ohne page (Nachladen und „Stelle merken“ schreiben sie per replaceState) und ohne Sprungmarke
function suchSchluessel(adresse) {
  const werte = new URLSearchParams(adresse.search);
  werte.delete('page');
  werte.delete('section_id');
  werte.sort();
  return `${adresse.pathname}?${werte}`;
}
let gezeichnet = suchSchluessel(location);

addEventListener('popstate', async () => {
  const sektion = document.querySelector('[data-gelo-sektion]');
  const schluessel = suchSchluessel(location);
  if (!sektion || schluessel === gezeichnet) return;
  const pfadVorher = gezeichnet.split('?')[0];
  gezeichnet = schluessel;
  if (pfadVorher !== location.pathname) return;
  blattSchliessen({ fokus: false });
  try {
    await sectionRenderer.renderSection(sektion.dataset.geloSektion);
  } catch (fehler) {
    console.warn('[gelo-kollektion] Zurück: Neuzeichnen nicht möglich, lade die Seite', fehler);
    location.reload();
    return;
  }
  // Horizon stellt die Scrollhöhe schon vor dem Neuzeichnen wieder her (scroll-container.js), bei kürzerer Liste zu früh
  const hoehe = history.state?.scrollTop;
  if (Number.isFinite(hoehe)) scrollBereich().scrollTo({ top: hoehe, behavior: 'instant' });
  // die Anzahl im Kopf sagt Screenreadern den neuen Stand an (aria-live)
  kategorieZeigen();
});

// ---------- Klicks ----------
document.addEventListener('click', (ereignis) => {
  const ziel = ereignis.target instanceof Element ? ereignis.target : null;
  if (!ziel) return;

  const seitenLink = ziel.closest('a[data-gelo-mehr], a[data-gelo-zurueck]');
  if (seitenLink) {
    // neuer Tab / neues Fenster bleibt dem Browser überlassen
    if (ereignis.button !== 0 || ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey || ereignis.altKey) return;
    ereignis.preventDefault();
    seiteLaden(seitenLink, seitenLink.hasAttribute('data-gelo-zurueck'));
    return;
  }

  const sortKnopf = ziel.closest('[data-gelo-sortieren]');
  if (sortKnopf) {
    blattOeffnen(sortKnopf);
    return;
  }

  const mehrtext = ziel.closest('[data-gelo-mehrtext]');
  if (mehrtext) {
    const text = mehrtext.closest('[data-gelo-text]');
    if (!text) return;
    if (offeneTexte.has(text.id)) offeneTexte.delete(text.id);
    else offeneTexte.add(text.id);
    texteAnpassen(text.parentElement || text);
    return;
  }

  // ---------- 3. Stelle merken ----------
  const teil = ziel.closest('.gm-kollektion .gm-karte a[href]')?.closest('li.gm-raster__teil[id]');
  if (teil) {
    const adresse = new URL(location.href);
    const seite = Number(teil.dataset.seite) || 1;
    if (seite > 1) adresse.searchParams.set('page', String(seite));
    else adresse.searchParams.delete('page');
    adresse.hash = teil.id;
    history.replaceState(history.state, '', adresse);
  }
});

// ---------- 10. Sortier-Liste per Tastatur ----------
// Horizon (assets/component.js) reicht handleKeyDown als event.target die Komponente selbst statt der Option, darum wählen
// Enter und Leertaste in SortingFilterComponent nichts. Hier vorher abgefangen (Capture-Phase am Dokument) und wie
// Horizons #selectOption erledigt: Radio klicken (Horizon zeichnet neu), Liste schließen, Fokus zurück auf den Knopf.
// Pfeiltasten und Escape bleiben bei Horizon.
document.addEventListener(
  'keydown',
  (ereignis) => {
    if (ereignis.key !== 'Enter' && ereignis.key !== ' ') return;
    if (ereignis.altKey || ereignis.ctrlKey || ereignis.metaKey) return;
    const ziel = ereignis.target instanceof Element ? ereignis.target : null;
    const option = ziel?.closest('.gm-kollektion .sorting-filter__option[role="option"]');
    const komponente = option?.closest('sorting-filter-component');
    const radio = option?.querySelector('input[type="radio"]');
    if (!option || !komponente || !(radio instanceof HTMLInputElement)) return;
    ereignis.preventDefault();
    ereignis.stopPropagation();
    komponente.querySelectorAll('.sorting-filter__option[role="option"]').forEach((eintrag) => {
      eintrag.setAttribute('aria-selected', String(eintrag === option));
      if (eintrag instanceof HTMLElement) eintrag.tabIndex = eintrag === option ? 0 : -1;
    });
    if (!radio.checked) radio.click();
    const details = option.closest('details');
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
      details.querySelector('summary')?.focus();
    }
  },
  true
);

// Das Blatt gehört nur unter 990 px dazu; wird das Fenster breiter, schließt es (oben steht dann Horizons Leiste)
DESK.addEventListener('change', (ereignis) => {
  if (ereignis.matches) blattSchliessen({ fokus: false });
});

// Mit #Karte in der Adresse (zurück von der Produktseite): Karte in den Blick holen. Lädt der Browser die Seite neu,
// stellt Horizon (scroll-container.js) erst die alte Scrollhöhe der längeren Liste wieder her, darum danach noch einmal.
// Aus dem Zwischenspeicher (bfcache) ist die ganze Liste noch da, dann bleibt Horizons Stelle.
function zurKarte() {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;
  const teil = document.getElementById(id);
  if (teil?.matches('.gm-raster__teil')) teil.scrollIntoView({ behavior: 'instant', block: 'center' });
}
// Kategorien als Wischzeile (unter 990 px, nur ohne Filter): ist die gewählte Kategorie nicht ganz zu sehen, rückt die
// Zeile auf eine Chip-Grenze – der Chip davor steht ganz am Anfang, wenn beide passen, sonst die gewählte selbst. Nur am
// Ende der Zeile (letzte Kategorien) bleibt links ein Rest stehen; die Ränder laufen dafür weich aus (CSS).
function kategorieZeigen() {
  document.querySelectorAll('.gm-kategorien ul').forEach((liste) => {
    const aktiv = liste.querySelector('[aria-current="page"]')?.closest('li');
    if (!aktiv || !liste.offsetParent || liste.scrollWidth <= liste.clientWidth + 1) return;
    const stil = getComputedStyle(liste);
    const links = parseFloat(stil.paddingLeft) || 0;
    const rechts = parseFloat(stil.paddingRight) || 0;
    const kasten = liste.getBoundingClientRect();
    const r = aktiv.getBoundingClientRect();
    if (r.left >= kasten.left + links - 1 && r.right <= kasten.right - rechts + 1) return;
    // Lage in der Zeile (mit Polster), unabhängig von der aktuellen Scrollstelle
    const lage = (li) => li.getBoundingClientRect().left - kasten.left + liste.scrollLeft;
    const max = liste.scrollWidth - liste.clientWidth;
    const sichtbarBei = (stelle) => lage(aktiv) - links >= stelle - 1 && lage(aktiv) + aktiv.offsetWidth <= stelle + liste.clientWidth - rechts + 1;
    const stellen = [aktiv.previousElementSibling, aktiv]
      .filter(Boolean)
      .map((li) => (li === liste.firstElementChild ? 0 : lage(li) - links));
    const ziel = stellen.find((stelle) => stelle <= max + 1 && sichtbarBei(stelle)) ?? max;
    liste.scrollTo({ left: ziel, behavior: 'instant' });
  });
}

function start() {
  document.querySelectorAll('[data-gelo-sektion]').forEach(beobachten);
  kategorieZeigen();
  zurKarte();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
addEventListener('pageshow', (ereignis) => {
  if (!ereignis.persisted) requestAnimationFrame(() => requestAnimationFrame(zurKarte));
});
